import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { RECORDING_FRAME_GOOGLE_1080P } from "../lib/recordingCaptureFrame";

/**
 * 1080p capture stage — same contain-fit pattern as FeatureGraphicPage `fillCapture`.
 * Fixed 1920×1080 design frame scaled into the browser window (letterbox, no crop).
 */
export default function RecordingCaptureStage({
    children,
    width = RECORDING_FRAME_GOOGLE_1080P.width,
    height = RECORDING_FRAME_GOOGLE_1080P.height,
    /** Pillarbox / letterbox fill behind the scaled frame. */
    stageBg = "#0B1424",
}) {
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const updateScale = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            setScale(Math.min(vw / width, vh / height));
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, [width, height]);

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        const root = document.getElementById("root");
        const prev = {
            htmlOverflow: html.style.overflow,
            bodyOverflow: body.style.overflow,
            bodyMargin: body.style.margin,
            bodyBg: body.style.background,
            rootOverflow: root?.style.overflow,
        };
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
        body.style.margin = "0";
        body.style.background = stageBg;
        if (root) root.style.overflow = "hidden";
        return () => {
            html.style.overflow = prev.htmlOverflow;
            body.style.overflow = prev.bodyOverflow;
            body.style.margin = prev.bodyMargin;
            body.style.background = prev.bodyBg;
            if (root) root.style.overflow = prev.rootOverflow || "";
        };
    }, [stageBg]);

    return (
        <Box
            data-sliding-capture="1080p"
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: stageBg,
                overflow: "hidden",
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width,
                    height,
                    transform: `translate(-50%, -50%) scale(${scale})`,
                    transformOrigin: "center center",
                    overflow: "hidden",
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        width,
                        height,
                        position: "relative",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        boxSizing: "border-box",
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
}
